import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ResponsivePreview.css';

const MOBILE_WIDTH = 390;
const MOBILE_HEIGHT = 844;
const DESKTOP_VIEWPORTS = {
  pc: { width: 1440, minHeight: 900, label: '1440px desktop' },
  laptop: { width: 1366, minHeight: 768, label: '1366 × 768 laptop' },
};

function ResponsivePreview() {
  const [view, setView] = useState('pc');
  const isPhonePreview = view === 'mobile' || view === 'low';
  const desktopViewport = DESKTOP_VIEWPORTS[view] || DESKTOP_VIEWPORTS.pc;
  const isDesktopPreview = view === 'pc' || view === 'laptop';
  const stageRef = useRef(null);
  const [desktopFrame, setDesktopFrame] = useState({ scale: 1, height: DESKTOP_VIEWPORTS.pc.minHeight });
  const appUrl = useMemo(() => {
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('preview', 'embed');
    if (view === 'low') url.searchParams.set('performance', 'low');
    url.hash = window.location.hash || '#collection';
    return url.toString();
  }, [view]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !isDesktopPreview) return undefined;

    const updateDesktopFrame = () => {
      const bounds = stage.getBoundingClientRect();
      const scale = Math.min(1, Math.max(0.38, (bounds.width - 24) / desktopViewport.width));
      const height = view === 'laptop'
        ? desktopViewport.minHeight
        : Math.max(desktopViewport.minHeight, (bounds.height - 24) / scale);
      setDesktopFrame({ scale, height });
    };

    updateDesktopFrame();
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateDesktopFrame)
      : null;
    observer?.observe(stage);
    window.addEventListener('resize', updateDesktopFrame);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateDesktopFrame);
    };
  }, [desktopViewport.minHeight, desktopViewport.width, isDesktopPreview, view]);

  return (
    <main className={`responsive-preview responsive-preview--${view}`}>
      <header className="responsive-preview__toolbar">
        <div className="responsive-preview__brand">
          <span className="responsive-preview__eyebrow">SOLYMINX CATALOG</span>
          <span className="responsive-preview__title">Preview</span>
        </div>
        <div className="responsive-preview__controls" role="group" aria-label="Choose preview size">
          <button type="button" className={view === 'pc' ? 'is-active' : ''} aria-pressed={view === 'pc'} onClick={() => setView('pc')}>
            <span aria-hidden="true">▣</span> PC view
          </button>
          <button type="button" className={view === 'laptop' ? 'is-active' : ''} aria-pressed={view === 'laptop'} onClick={() => setView('laptop')}>
            <span aria-hidden="true">▰</span> Laptop
          </button>
          <button type="button" className={view === 'mobile' ? 'is-active' : ''} aria-pressed={view === 'mobile'} onClick={() => setView('mobile')}>
            <span aria-hidden="true">▯</span> Mobile view
          </button>
          <button type="button" className={view === 'low' ? 'is-active' : ''} aria-pressed={view === 'low'} onClick={() => setView('low')}>
            <span aria-hidden="true">⚡</span> Low mode
          </button>
        </div>
        <span className="responsive-preview__size">
          {view === 'low' ? `Lag-safe · ${MOBILE_WIDTH} × ${MOBILE_HEIGHT}` : view === 'mobile' ? `${MOBILE_WIDTH} × ${MOBILE_HEIGHT}` : desktopViewport.label}
        </span>
      </header>
      <section ref={stageRef} className="responsive-preview__stage" aria-label={`${view} preview`}>
        {isDesktopPreview ? (
          <div
            className="responsive-preview__desktop-shell"
            style={{
              width: desktopViewport.width * desktopFrame.scale,
              height: desktopFrame.height * desktopFrame.scale,
            }}
          >
            <iframe
              key={view}
              className="responsive-preview__frame responsive-preview__frame--desktop"
              title={`Solyminx catalog ${view} preview`}
              src={appUrl}
              style={{
                width: desktopViewport.width,
                height: desktopFrame.height,
                transform: `scale(${desktopFrame.scale})`,
              }}
            />
          </div>
        ) : (
          <iframe
            key={view}
            className="responsive-preview__frame"
            title={`Solyminx catalog ${view} preview`}
            src={appUrl}
            style={isPhonePreview ? { width: MOBILE_WIDTH, height: MOBILE_HEIGHT } : undefined}
          />
        )}
      </section>
    </main>
  );
}

export default ResponsivePreview;
